import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useDispatch, useSelector } from "react-redux";
import {
  CustomerName,
  toggleEditModel,
  toggleChangePasswordModel,
} from "../Customer";
import { signOut } from "../Security";
import { isSignedInSelector } from "ccui-framework";

const Item = styled.a`
  cursor: pointer;
  font-family: Montserrat, serif;
  height: 25px;
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: -0.2px;
  color: #777984;
  padding: 1rem;

  &:hover {
    text-decoration: none;
    color: #1f2021;
  }
`;

const Menu = styled.div`
  position: absolute;
  top: 2rem;
  width: 250px;
  background: #ffffff;
  box-shadow: 0px 0px 50px rgba(31, 32, 33, 0.08);
  right: 1rem;
  flex-direction: column;
  align-items: left;
  padding: 1rem 0 2rem 2rem;
  display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
  z-index: 1000;
`;

const DropdownArrow = styled.span`
  margin-left: 0.5rem;
  cursor: pointer;
`;

const Logo = styled.img`
  height: 2rem;
`;

const Container = styled.div`
  display: flex;
  justify-content: ${({ isSignedIn }) =>
    !isSignedIn ? "center" : "space-between"};
  flex: 0 0 100%;
  align-items: center;
  background: #ffffff;
  box-shadow: 0 1px 0 #e1eaed;
  @media (min-width: 768px) {
    padding-right: ${({ isSignedIn }) => (!isSignedIn ? "0" : "5rem")};
  }
  max-height: 65px;
  flex-shrink: 0;
  border-bottom: 1px solid #e1eaed;
  position: relative;
`;

/* Desktop: logo on the left inside a fixed-width block */
const DesktopLeft = styled.div`
  display: none;
  @media (min-width: 768px) {
    display: flex;
    width: 250px;
    justify-content: flex-end;
  }
`;

/* Mobile: 3-column layout */
const MobileLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 0.5rem;
  position: relative;
  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileCenter = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  &:focus {
    outline: none;
  }
`;

const DesktopRight = styled.div`
  display: none;
  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    position: relative;
    cursor: pointer;
  }
`;

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);
  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isDesktop;
};

const useHeaderDropdown = () => {
  const [isDropDownMenuOpen, setDropDownMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const listener = (event) => {
      if (menuRef.current && menuRef.current.contains(event.target)) return;
      setDropDownMenuOpen(false);
    };
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, []);

  return [isDropDownMenuOpen, setDropDownMenuOpen, menuRef];
};

const DropdownMenu = ({ isOpen, dispatch, setDropDownMenuOpen }) => (
  <div style={{ position: "relative" }}>
    <Menu isOpen={isOpen}>
      <Item
        onClick={() => {
          dispatch(toggleChangePasswordModel());
          setDropDownMenuOpen(false);
        }}
      >
        Change password
      </Item>
      <Item
        onClick={() => {
          dispatch(toggleEditModel());
          setDropDownMenuOpen(false);
        }}
      >
        Profile
      </Item>
      <Item onClick={() => dispatch(signOut())}>Log out</Item>
    </Menu>
  </div>
);

const Header = ({ onMenuToggle, isSidebarOpen }) => {
  const dispatch = useDispatch();
  const isSignedIn = useSelector((state) => isSignedInSelector(state));
  const isDesktop = useIsDesktop();
  const [isDropDownMenuOpen, setDropDownMenuOpen, menuRef] =
    useHeaderDropdown();

  const dropdown = (
    <DropdownMenu
      isOpen={isDropDownMenuOpen}
      dispatch={dispatch}
      setDropDownMenuOpen={setDropDownMenuOpen}
    />
  );

  return (
    <Container id="header" isSignedIn={isSignedIn}>
      {/* Desktop layout */}
      {isSignedIn && (
        <DesktopLeft>
          <Logo
            src="https://cdn.cardcash.com/website/premier/cardcash_premier.svg"
            alt="Premier Logo"
          />
        </DesktopLeft>
      )}
      {!isSignedIn && (
        <Logo
          src="https://cdn.cardcash.com/website/premier/cardcash_premier.svg"
          alt="Premier Logo"
        />
      )}
      {isSignedIn && isDesktop && (
        <DesktopRight
          ref={menuRef}
          onClick={() => setDropDownMenuOpen((o) => !o)}
        >
          <CustomerName />
          <DropdownArrow>
            <svg
              width="10"
              height="6"
              viewBox="0 0 10 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.183058 0.183058C0.400016 -0.0338997 0.736797 -0.0580061 0.980382 0.110739L1.06694 0.183058L4.625 3.742L8.18306 0.183058C8.40002 -0.0338997 8.7368 -0.0580061 8.98038 0.110739L9.06694 0.183058C9.2839 0.400016 9.30801 0.736797 9.13926 0.980382L9.06694 1.06694L5.06694 5.06694C4.84998 5.2839 4.5132 5.30801 4.26962 5.13926L4.18306 5.06694L0.183058 1.06694C-0.0610194 0.822864 -0.0610194 0.427136 0.183058 0.183058Z"
                fill="#777984"
              />
            </svg>
          </DropdownArrow>
          {dropdown}
        </DesktopRight>
      )}

      {/* Mobile layout */}
      {isSignedIn && !isDesktop && (
        <MobileLayout>
          <IconButton onClick={onMenuToggle} aria-label="Toggle menu">
            {isSidebarOpen ? (
              <img src="/close_24px.svg" alt="Close menu" />
            ) : (
              <img src="/menu_24px.svg" alt="Menu" />
            )}
          </IconButton>
          <MobileCenter>
            <Logo src="/premier_logo_30px.svg" alt="Premier Logo" />
          </MobileCenter>
          <div ref={menuRef} style={{ position: "relative" }}>
            <IconButton
              onClick={() => setDropDownMenuOpen((o) => !o)}
              aria-label="Profile menu"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="8"
                  r="4"
                  stroke="#777984"
                  strokeWidth="1.5"
                />
                <path
                  d="M4 20c0-4 3.582-7 8-7s8 3 8 7"
                  stroke="#777984"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </IconButton>
            {dropdown}
          </div>
        </MobileLayout>
      )}
    </Container>
  );
};

Header.propTypes = {};

export { Header };
